import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BadgePercent,
  Check,
  FileText,
  Layers,
  Link2,
  ListOrdered,
  MessageCircle,
  MousePointerClick,
  Pencil,
  Plus,
  Rocket,
  SlidersHorizontal,
  Sparkles,
  Star,
  TerminalSquare,
  Upload,
  X,
} from "lucide-react";
import { ideaFor, ideas, type CreationIntent } from "../shared/creation";
import type {
  Account,
  Asset,
  Mode,
  Project,
  ProjectDetail,
  StudioConfig,
  VideoChoices,
} from "../shared/types";
import { DEFAULT_CHOICES } from "../shared/catalog";
import { api, formatSize, patch, post, uploadFiles } from "./api";
import { DropVeil, IconButton, useFileDrop } from "./components";
import { changedChoices, VideoSettings } from "./VideoSettings";
import { useWallet } from "./Credits";
import { bilingual, useLang, useText } from "./i18n";

const TEXT = bilingual({
  en: {
    mode: {
      label: "Workspace mode",
      director: "Director",
      claude: "Your Claude",
    },
    starts: { label: "Starting points" },
    title: {
      rename: "Rename video",
      renameNamed: (title: string) => `Rename video: ${title}`,
      field: "Video title",
      save: "Save title",
      cancel: "Cancel rename",
    },
    composer: {
      tooMany: "Add up to 10 files at a time.",
      tooBig: "Each file can be up to 25 MB.",
      noRoom: "There’s room for 250 MB of files per video.",
      noDirector: "The Director isn't set up on this studio yet.",
      noDirectorClaude:
        "The Director isn't set up on this studio yet. Your Claude works with your own account.",
      opening: "Opening",
      adding: "Adding files",
      sending: "Sending",
      sourceMissing: "This product is unavailable",
      reusing: "Reusing",
      files: (n: number) => (n === 1 ? "1 file" : `${n} files`),
      sourceOpening: "Opening product…",
      removeSource: "Remove product context",
      removeIdea: "Remove starting point",
      message: "Message your director",
      placeholderSource: "What should this one sell?",
      placeholder: "Paste a product link and tell me what you need",
      native: "Claude Code, signed in with your own account.",
      filesToAdd: "Files to add",
      removeFile: (name: string) => `Remove ${name}`,
      drop: "Drop assets",
      add: "Add assets",
      dropIdeas: "Screens · logo · photos · a short to riff on",
      attach: "Attach files",
      settings: "Video settings",
      make: "Make video",
      openClaude: "Open Your Claude",
    },
  },
  es: {
    mode: {
      label: "Modo del estudio",
      director: "Director",
      claude: "Tu Claude",
    },
    starts: { label: "Puntos de partida" },
    title: {
      rename: "Renombrar vídeo",
      renameNamed: (title: string) => `Renombrar vídeo: ${title}`,
      field: "Título del vídeo",
      save: "Guardar título",
      cancel: "Cancelar",
    },
    composer: {
      tooMany: "Añade hasta 10 archivos a la vez.",
      tooBig: "Cada archivo puede ocupar hasta 25 MB.",
      noRoom: "Cada vídeo tiene espacio para 250 MB de archivos.",
      noDirector: "El director aún no está configurado en este estudio.",
      noDirectorClaude:
        "El director aún no está configurado en este estudio. Tu Claude funciona con tu propia cuenta.",
      opening: "Abriendo",
      adding: "Añadiendo archivos",
      sending: "Enviando",
      sourceMissing: "Este producto no está disponible",
      reusing: "Reutilizando",
      files: (n: number) => (n === 1 ? "1 archivo" : `${n} archivos`),
      sourceOpening: "Abriendo producto…",
      removeSource: "Quitar el contexto del producto",
      removeIdea: "Quitar el punto de partida",
      message: "Escribe a tu director",
      placeholderSource: "¿Qué debe vender este vídeo?",
      placeholder: "Pega el enlace de un producto y dime qué necesitas",
      native: "Claude Code, con la sesión de tu propia cuenta.",
      filesToAdd: "Archivos para añadir",
      removeFile: (name: string) => `Quitar ${name}`,
      drop: "Suelta archivos",
      add: "Añadir archivos",
      dropIdeas: "Capturas · logo · fotos · un vídeo de referencia",
      attach: "Adjuntar archivos",
      settings: "Ajustes del vídeo",
      make: "Crear vídeo",
      openClaude: "Abrir Tu Claude",
    },
  },
});

// Preferences are conveniences, never prerequisites for using the studio.
export function rememberedMode(uid: string): Mode {
  try {
    return localStorage.getItem(`cutline:mode:${uid}`) === "terminal"
      ? "terminal"
      : "chat";
  } catch {
    return "chat";
  }
}
export function rememberMode(uid: string, mode: Mode) {
  try {
    localStorage.setItem(`cutline:mode:${uid}`, mode);
  } catch {
    /* Private browsers may disallow storage. */
  }
}
export function ModeSwitch({
  mode,
  onChange,
  disabled = false,
}: {
  mode: Mode;
  onChange: (mode: Mode) => void;
  disabled?: boolean;
}) {
  const t = useText(TEXT).mode;
  return (
    <div className="seg mode-switch" role="group" aria-label={t.label}>
      <button
        type="button"
        aria-pressed={mode === "chat"}
        disabled={disabled}
        onClick={() => onChange("chat")}
      >
        <MessageCircle size={15} /> {t.director}
      </button>
      <button
        type="button"
        aria-pressed={mode === "terminal"}
        disabled={disabled}
        onClick={() => onChange("terminal")}
      >
        <TerminalSquare size={15} /> {t.claude}
      </button>
    </div>
  );
}

export const IDEA_ICONS: Record<CreationIntent, typeof Rocket> = {
  intro: Rocket,
  tutorial: ListOrdered,
  tour: MousePointerClick,
  offer: BadgePercent,
  feature: Sparkles,
  proof: Star,
  series: Layers,
};
export function StartingPoints({
  selected,
  onSelect,
  disabled,
}: {
  selected: CreationIntent | null;
  onSelect: (intent: CreationIntent | null) => void;
  disabled?: boolean;
}) {
  const t = useText(TEXT).starts,
    language = useLang();
  return (
    <div className="starts" role="group" aria-label={t.label}>
      {ideas(language).map((idea) => {
        const Icon = IDEA_ICONS[idea.id];
        return (
          <button
            type="button"
            className="chip"
            key={idea.id}
            aria-pressed={selected === idea.id}
            disabled={disabled}
            onClick={() => onSelect(selected === idea.id ? null : idea.id)}
          >
            <Icon size={15} /> {idea.label}
          </button>
        );
      })}
    </div>
  );
}

export function InlineTitle({
  title,
  onSave,
}: {
  title: string;
  onSave: (title: string) => Promise<boolean>;
}) {
  const t = useText(TEXT).title;
  const [editing, setEditing] = useState(false),
    [value, setValue] = useState(title),
    [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) {
      input.current?.focus();
      input.current?.select();
    }
  }, [editing]);
  if (!editing)
    return (
      <h1 className="editable-title" aria-label={title}>
        <button
          title={t.rename}
          aria-label={t.renameNamed(title)}
          onClick={() => {
            setValue(title);
            setEditing(true);
          }}
        >
          <span>{title}</span>
          <Pencil size={12} />
        </button>
      </h1>
    );
  return (
    <form
      className="title-editor"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!value.trim() || busy) return;
        setBusy(true);
        try {
          if (value.trim() === title || (await onSave(value.trim())))
            setEditing(false);
        } finally {
          setBusy(false);
        }
      }}
    >
      <input
        ref={input}
        aria-label={t.field}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={100}
        required
        disabled={busy}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !busy) {
            e.preventDefault();
            setEditing(false);
          }
        }}
      />
      <IconButton type="submit" label={t.save} disabled={busy || !value.trim()}>
        <Check size={16} />
      </IconButton>
      <IconButton
        label={t.cancel}
        disabled={busy}
        onClick={() => setEditing(false)}
      >
        <X size={15} />
      </IconButton>
    </form>
  );
}

function FileThumb({ file }: { file: File }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!file.type.startsWith("image/")) return;
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url ? (
    <img className="file-thumb" src={url} alt="" />
  ) : (
    <FileText size={14} />
  );
}

interface Draft {
  text: string;
  requestId: string;
  messageId: string;
  projectId?: string;
}
const draftKey = (uid: string, sourceId = "") =>
  `cutline:new:${uid}:${sourceId || "fresh"}`;
/** The brief waiting in the studio composer, if any. */
export const savedBrief = (uid: string) => readDraft(draftKey(uid)).text.trim();
function readDraft(key: string, initial = ""): Draft {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) || "null");
    if (
      saved &&
      typeof saved.text === "string" &&
      typeof saved.requestId === "string" &&
      typeof saved.messageId === "string"
    )
      return saved;
  } catch {
    /* A fresh draft is still available. */
  }
  return {
    text: initial,
    requestId: crypto.randomUUID(),
    messageId: crypto.randomUUID(),
  };
}

/** The studio composer. The first send creates the video and starts work. */
export function Composer({
  account,
  config,
  onCreated,
}: {
  account: Account;
  config: StudioConfig;
  onCreated: () => void;
}) {
  const t = useText(TEXT).composer,
    language = useLang();
  const { wallet } = useWallet();
  const navigate = useNavigate(),
    location = useLocation(),
    [params, setParams] = useSearchParams();
  const sourceId = params.get("from") || "";
  const storageKey = draftKey(account.id, sourceId);
  const [draft, setDraft] = useState(() =>
    location.state?.draft !== undefined
      ? ({
          text: String(location.state.draft),
          requestId: crypto.randomUUID(),
          messageId: crypto.randomUUID(),
        } as Draft)
      : readDraft(storageKey),
  );
  const intent = ideaFor(params.get("idea"))?.id || null;
  const [mode, setMode] = useState<Mode>(() =>
    config.nativeClaude ? rememberedMode(account.id) : "chat",
  );
  const [choices, setChoices] = useState<VideoChoices>(DEFAULT_CHOICES),
    [choosing, setChoosing] = useState(false);
  const changed = changedChoices(choices);
  const [source, setSource] = useState<ProjectDetail | null>(null),
    [sourceError, setSourceError] = useState("");
  const [files, setFiles] = useState<File[]>([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [step, setStep] = useState("");
  const submitting = useRef(false),
    fileInput = useRef<HTMLInputElement>(null),
    input = useRef<HTMLTextAreaElement>(null);
  // One retry-safe id per upload batch, renewed whenever the files change.
  const uploadIds = useRef<string[]>([]);
  const idea = ideaFor(intent, language);
  const IdeaIcon = intent ? IDEA_ICONS[intent] : null;
  useEffect(() => {
    if (location.state?.draft !== undefined)
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
  }, [location.state, location.pathname, location.search, navigate]);
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(draft));
    } catch {
      /* In-memory draft remains usable. */
    }
  }, [draft, storageKey]);
  useEffect(() => {
    if (!sourceId) return;
    let cancelled = false;
    api<ProjectDetail>(`/projects/${sourceId}`)
      .then((d) => {
        if (cancelled) return;
        setSource(d);
        // A product keeps its format, narrator and Director across videos.
        setChoices((c) => ({
          ...c,
          settings: { ...d.project.settings, direction: "" },
          voice: d.project.voice,
          model: d.project.model,
        }));
      })
      .catch((e) => {
        if (!cancelled) setSourceError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);
  useEffect(() => {
    if (
      mode === "chat" &&
      location.pathname === "/new" &&
      matchMedia("(pointer:fine)").matches
    )
      input.current?.focus();
  }, [mode, location.pathname]);
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [draft.text, mode]);
  const chooseMode = (next: Mode) => {
    setMode(next);
    rememberMode(account.id, next);
    setError("");
  };
  const chooseIdea = (next: CreationIntent | null) => {
    const p = new URLSearchParams(params);
    if (next) p.set("idea", next);
    else p.delete("idea");
    setParams(p, { replace: true, state: location.state });
    if (mode === "chat") input.current?.focus({ preventScroll: true });
  };
  const addFiles = (incoming: FileList | null) => {
    if (!incoming?.length || submitting.current) return;
    const next = [...files];
    for (const file of Array.from(incoming))
      if (
        !next.some(
          (f) =>
            f.name === file.name &&
            f.size === file.size &&
            f.lastModified === file.lastModified,
        )
      )
        next.push(file);
    if (next.length > 10) {
      setError(t.tooMany);
      return;
    }
    if (next.some((f) => f.size > 25 * 1024 * 1024)) {
      setError(t.tooBig);
      return;
    }
    if (
      next.reduce((n, f) => n + f.size, 0) +
        (source?.assets.reduce((n, f) => n + f.size, 0) || 0) >
      250 * 1024 * 1024
    ) {
      setError(t.noRoom);
      return;
    }
    uploadIds.current = [];
    setFiles(next);
    setError("");
  };
  const finish = (project: Project, followUp = "") => {
    try {
      sessionStorage.removeItem(storageKey);
      if (followUp)
        sessionStorage.setItem(
          `cutline:draft:${account.id}:${project.id}`,
          followUp,
        );
    } catch {
      /* Optional browser storage. */
    }
    onCreated();
    navigate(`/project/${project.id}`, { replace: true });
  };
  const send = async (event?: FormEvent) => {
    event?.preventDefault();
    if (
      submitting.current ||
      (mode === "chat" && !draft.text.trim()) ||
      (sourceId && !source)
    )
      return;
    // Without credits, the brief waits while the customer gets some.
    if (account.plan !== "preview" && wallet && wallet.available <= 0) {
      navigate("/plans", { state: { brief: draft.text } });
      return;
    }
    if (mode === "chat" && !config.director) {
      setError(config.nativeClaude ? t.noDirectorClaude : t.noDirector);
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      setStep(t.opening);
      const { project } = await post<{ project: Project }>("/projects", {
        mode,
        intent,
        ...choices,
        requestId: draft.requestId,
        ...(sourceId ? { sourceProjectId: sourceId } : {}),
      });
      setDraft((d) => ({ ...d, projectId: project.id }));
      // A lost HTTP response must not create a second project or paid turn.
      const existing = await api<ProjectDetail>(`/projects/${project.id}`);
      const accepted = existing.messages.find((m) => m.id === draft.messageId);
      if (accepted) {
        finish(
          existing.project,
          accepted.text !== draft.text.trim() ? draft.text : "",
        );
        return;
      }
      if (project.mode !== mode || project.intent !== intent)
        await patch(`/projects/${project.id}`, { mode, intent });
      // A retried creation returns the first project, with the first choices.
      if (
        Object.entries(choices).some(
          ([k, v]) =>
            JSON.stringify(v) !==
            JSON.stringify(project[k as keyof VideoChoices]),
        )
      )
        await patch(`/projects/${project.id}/production`, choices);
      if (files.length) {
        setStep(t.adding);
        await uploadFiles(project.id, files, uploadIds.current);
        setFiles([]);
        uploadIds.current = [];
      }
      if (mode === "chat") {
        setStep(t.sending);
        await post(`/projects/${project.id}/messages`, {
          text: draft.text,
          requestId: draft.messageId,
        });
      }
      finish(project, mode === "terminal" ? draft.text : "");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      submitting.current = false;
      setBusy(false);
      setStep("");
    }
  };
  // Back from choosing a plan: the customer asked to make the saved brief.
  const make = !!location.state?.make;
  useEffect(() => {
    if (!make || (sourceId && !source)) return;
    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    });
    if (draft.text.trim()) void send();
  }, [make, source]);
  const dragging = useFileDrop(addFiles, !busy);
  const blocked = busy || (!!sourceId && !source);
  return (
    <div className="creation">
      <form
        className={`composer ${busy ? "is-busy" : ""} ${dragging ? "is-dragging" : ""} composer-${mode}`}
        onSubmit={send}
        aria-busy={busy}
      >
        {dragging && <DropVeil />}
        {sourceId && (
          <div className={`source ${sourceError ? "source-error" : ""}`}>
            <FileText size={16} />
            <span>
              {sourceError ? (
                t.sourceMissing
              ) : source ? (
                <>
                  {t.reusing}{" "}
                  <b>{source.project.brand || source.project.title}</b>
                  {source.assets.length
                    ? ` · ${t.files(source.assets.length)}`
                    : ""}
                </>
              ) : (
                t.sourceOpening
              )}
            </span>
            <IconButton
              label={t.removeSource}
              disabled={busy || !!draft.projectId}
              onClick={() =>
                navigate(`/new${intent ? `?idea=${intent}` : ""}`, {
                  replace: true,
                  state: { draft: draft.text },
                })
              }
            >
              <X size={15} />
            </IconButton>
          </div>
        )}
        <div className="composer-field">
          {idea && IdeaIcon && (
            <span className="tag">
              <IdeaIcon size={13} />
              {idea.label}
              <button
                type="button"
                aria-label={t.removeIdea}
                disabled={busy}
                onClick={() => chooseIdea(null)}
              >
                <X size={11} strokeWidth={2.6} />
              </button>
            </span>
          )}
          {mode === "chat" ? (
            <>
              {!idea && (
                <Link2 size={18} className="field-icon" aria-hidden="true" />
              )}
              <textarea
                ref={input}
                aria-label={t.message}
                placeholder={source ? t.placeholderSource : t.placeholder}
                value={draft.text}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, text: e.target.value }))
                }
                rows={1}
                maxLength={20000}
                disabled={busy}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !e.shiftKey &&
                    !e.nativeEvent.isComposing
                  ) {
                    e.preventDefault();
                    void send();
                  }
                }}
              />
            </>
          ) : (
            <p className="native-line">
              <TerminalSquare size={18} aria-hidden="true" />
              {t.native}
            </p>
          )}
        </div>
        {files.length > 0 && (
          <ul className="draft-files" aria-label={t.filesToAdd}>
            {files.map((file, i) => (
              <li key={`${file.name}-${i}`}>
                <FileThumb file={file} />
                <span>{file.name}</span>
                <small className="mono">{formatSize(file.size)}</small>
                <button
                  type="button"
                  aria-label={t.removeFile(file.name)}
                  disabled={busy}
                  onClick={() => {
                    setFiles((f) => f.filter((_, n) => n !== i));
                    uploadIds.current = [];
                  }}
                >
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="composer-bar">
          <button
            type="button"
            className="dropzone"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            <Upload size={15} />
            <b className="fine">{t.drop}</b>
            <b className="coarse">{t.add}</b>
            <span className="drop-ideas">{t.dropIdeas}</span>
          </button>
          <button
            type="button"
            className="add-files"
            aria-label={t.attach}
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            <Plus size={18} />
            {files.length > 0 && <b>{files.length}</b>}
          </button>
          <button
            type="button"
            className={`settings-button ${changed.length ? "on" : ""}`}
            aria-haspopup="dialog"
            aria-label={`${t.settings}${changed.length ? `: ${changed.join(", ")}` : ""}`}
            title={changed.length ? changed.join(" · ") : undefined}
            disabled={busy}
            onClick={() => setChoosing(true)}
          >
            <SlidersHorizontal size={15} />
            <span>{changed.length ? changed.join(" · ") : t.settings}</span>
          </button>
          {config.nativeClaude && (
            <ModeSwitch mode={mode} onChange={chooseMode} disabled={busy} />
          )}
          {mode === "chat" ? (
            <button
              className="button primary make"
              disabled={blocked || !draft.text.trim()}
            >
              {busy ? step : t.make}
              {!busy && <ArrowRight size={16} />}
            </button>
          ) : (
            <button
              type="submit"
              className="button primary make"
              disabled={blocked}
            >
              {busy ? step : t.openClaude}
              {!busy && <ArrowUpRight size={16} />}
            </button>
          )}
        </div>
      </form>
      <StartingPoints selected={intent} onSelect={chooseIdea} disabled={busy} />
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {choosing && (
        <VideoSettings
          value={choices}
          preview={config.preview}
          onSave={setChoices}
          onClose={() => setChoosing(false)}
        />
      )}
      {error && (
        <p className="form-error creation-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
